/**
 * src/components/spatial-bindings/BindOrganizationDialog.tsx
 * One dialog for binding an organization to a spatial object, entered from
 * either side.
 *
 * From the organization: the organization is fixed, pick an object.
 * From the object: the object is fixed, pick an organization.
 *
 * The same component both ways because the hard parts — deciding whether
 * this is a proposal, an approval or a reassignment, explaining what a
 * partly-verified organization will and will not be able to do, and
 * registering an object that exists only in GeoLink — are identical
 * whichever direction the operator came from. Two forms would have drifted.
 *
 * What the button does is never assumed. `classifyBindAction` reads the
 * chain's current record and returns the one transition the contract will
 * actually accept, so a live approved binding offers a reassignment rather
 * than a bind that would fail with BindingAlreadyExists.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getOrganization,
  listOrganizations,
  shortOrgId,
  type Organization,
} from '../../v2/services/organizationService';
import {
  buildApproveBinding,
  buildCancelReassign,
  buildCompleteReassign,
  buildProposeBinding,
  buildProposeReassign,
  buildRegisterObject,
  getSpatialObject,
  objectIdToUuid,
  normalizeObjectId,
  shortObjectId,
  RELATIONSHIP,
  RELATIONSHIP_LABEL,
  type RelationshipName,
  type SpatialObjectRecord,
  type SpatialSigner,
} from '../../v2/services/spatialRegistryService';
import {
  classifyBindAction,
  countryMatches,
  deriveEligibility,
  describeLimitations,
  relationshipLabel,
  type BindAction,
} from '../../v2/services/spatialBindingModel';
import { numericToAlpha2, resolveCountry } from '../../v2/config';
import type { SpatialObjectAdminRow } from '../../services/spatial/geoLinkSpatialObjectsAdminClient';
import { listCandidateObjects, readMirrorOrgId } from './objectCandidates';
import {
  Banner, button, CapabilityList, hint, input, ModalShell, mono, Pill, sectionTitle,
} from './bindingUi';
import { TX_PHASE_LABEL, useSpatialWrite } from './useSpatialWrite';

const HEX64 = /^[0-9a-f]{64}$/i;

export interface BindDialogObject {
  /** GeoLink UUID. */
  id: string;
  name: string | null;
  objectType?: string | null;
  countryIso2?: string | null;
  parentObjectId?: string | null;
  organizationId?: string | null;
}

export interface BindOrganizationDialogProps {
  mode: 'from-org' | 'from-object';
  /** Fixed in `from-org` mode. */
  organization?: Organization | null;
  /** Fixed in `from-object` mode. */
  object?: BindDialogObject | null;
  /** Numeric ISO country used to scope the pickers. */
  countryCode: number | null;
  signer: SpatialSigner | null;
  walletAddress: string | null;
  networkPassphrase?: string;
  onClose: () => void;
  /** Called after a write lands, so the host can re-read its own state. */
  onCompleted: () => void | Promise<void>;
}

export const BindOrganizationDialog: React.FC<BindOrganizationDialogProps> = ({
  mode,
  organization: fixedOrg = null,
  object: fixedObject = null,
  countryCode,
  signer,
  walletAddress,
  networkPassphrase,
  onClose,
  onCompleted,
}) => {
  const write = useSpatialWrite(signer, networkPassphrase);

  // ── Selection ──────────────────────────────────────────────────────────────

  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(fixedOrg);
  const [selectedObject, setSelectedObject] = useState<BindDialogObject | null>(fixedObject);
  const [relationship, setRelationship] = useState<RelationshipName>('owner');
  const [reasonHash, setReasonHash] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showRawId, setShowRawId] = useState(false);
  const [rawObjectId, setRawObjectId] = useState('');

  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [objects, setObjects] = useState<SpatialObjectAdminRow[]>([]);
  const [objectQuery, setObjectQuery] = useState('');
  const [listError, setListError] = useState<string | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [countryScoped, setCountryScoped] = useState(false);
  const [countryDataIncomplete, setCountryDataIncomplete] = useState(false);

  /** The chain's record for the currently selected object. */
  const [record, setRecord] = useState<SpatialObjectRecord | null>(null);
  const [recordLoaded, setRecordLoaded] = useState(false);

  const alpha2 = useMemo(
    () => (countryCode ? numericToAlpha2(countryCode) : fixedObject?.countryIso2 ?? null),
    [countryCode, fixedObject],
  );

  // Organizations for the picker, scoped to the object's country. Loaded
  // only in from-object mode; the other direction already has one.
  useEffect(() => {
    if (mode !== 'from-object') return;
    let cancelled = false;
    setListLoading(true);
    listOrganizations(countryCode !== null ? { country: countryCode } : {})
      .then((r) => { if (!cancelled) setOrgs(r.organizations); })
      .catch((e) => { if (!cancelled) setListError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setListLoading(false); });
    return () => { cancelled = true; };
  }, [mode, countryCode]);

  // Objects for the picker.
  //
  // The chain cannot list objects at all, so GeoLink is the only
  // enumeration source; an object GeoLink has never seen cannot be
  // offered. Country scoping is best-effort — see objectCandidates for why
  // an empty country-filtered result is not trusted.
  useEffect(() => {
    if (mode !== 'from-org') return;
    let cancelled = false;
    setListLoading(true);
    setListError(null);
    listCandidateObjects(alpha2)
      .then(({ rows, countryScoped, countryDataIncomplete }) => {
        if (cancelled) return;
        setObjects(rows);
        setCountryScoped(countryScoped);
        setCountryDataIncomplete(countryDataIncomplete);
      })
      .catch((e) => {
        if (!cancelled) {
          setListError(
            e instanceof Error ? e.message : 'Could not load spatial objects from GeoLink',
          );
        }
      })
      .finally(() => { if (!cancelled) setListLoading(false); });
    return () => { cancelled = true; };
  }, [mode, alpha2]);

  // Read the chain whenever the chosen object changes. A null record means
  // "exists in GeoLink, not on chain", which is the normal starting state.
  const loadRecord = useCallback(async () => {
    if (!selectedObject?.id) { setRecord(null); setRecordLoaded(false); return; }
    setRecordLoaded(false);
    const r = await getSpatialObject(selectedObject.id);
    setRecord(r);
    setRecordLoaded(true);
  }, [selectedObject?.id]);

  useEffect(() => { void loadRecord(); }, [loadRecord]);

  // ── Derived state ──────────────────────────────────────────────────────────

  const eligibility = useMemo(() => deriveEligibility(selectedOrg), [selectedOrg]);
  const limitations = useMemo(
    () => describeLimitations(selectedOrg, eligibility),
    [selectedOrg, eligibility],
  );

  const action: BindAction | null = useMemo(
    () => (selectedObject && recordLoaded ? classifyBindAction(record, selectedOrg?.id) : null),
    [selectedObject, recordLoaded, record, selectedOrg?.id],
  );

  /** The object's country, from the chain when registered, GeoLink otherwise. */
  const objectNumericCountry = useMemo(() => {
    if (record?.numericCode) return record.numericCode;
    const iso = selectedObject?.countryIso2;
    if (!iso) return null;
    try { return resolveCountry(iso); } catch { return null; }
  }, [record, selectedObject]);

  const countryOk = useMemo(
    () => countryMatches(selectedOrg?.numericCode, objectNumericCountry),
    [selectedOrg, objectNumericCountry],
  );

  const filteredObjects = useMemo(() => {
    const q = objectQuery.trim().toLowerCase();
    const rows = q
      ? objects.filter((r) =>
          (r.name || '').toLowerCase().includes(q)
          || r.id.toLowerCase().startsWith(q)
          || (r.objectType || '').toLowerCase().includes(q))
      : objects;
    return rows.slice(0, 200);
  }, [objects, objectQuery]);

  const reasonHashValid = !reasonHash.trim() || HEX64.test(reasonHash.trim());
  const reasonHashArg = reasonHash.trim() ? reasonHash.trim().toLowerCase() : null;

  // ── Blocking conditions, stated rather than silently disabling ─────────────

  const blockers = useMemo(() => {
    const out: string[] = [];
    if (!selectedOrg) out.push('Choose an organization.');
    if (!selectedObject) out.push('Choose a spatial object.');
    if (!walletAddress || !signer) out.push('Connect the wallet that will sign this transaction.');
    if (selectedOrg && selectedObject && objectNumericCountry && !countryOk) {
      out.push(
        `${selectedOrg.name || 'This organization'} is registered in `
        + `${selectedOrg.alpha2 || selectedOrg.numericCode || 'another country'} but this object is in `
        + `${numericToAlpha2(objectNumericCountry) || objectNumericCountry}. The registry rejects a `
        + 'cross-country binding at approval.',
      );
    }
    if (selectedOrg?.statusName === 'revoked') {
      out.push('This organization is revoked, which is terminal on chain.');
    }
    if (!reasonHashValid) out.push('The reference hash must be 64 hex characters.');
    return out;
  }, [
    selectedOrg, selectedObject, walletAddress, signer,
    objectNumericCountry, countryOk, reasonHashValid,
  ]);

  const actionable = action
    && !blockers.length
    && !['already_bound', 'blocked'].includes(action.kind);

  // ── Execution ──────────────────────────────────────────────────────────────

  const [registered, setRegistered] = useState(false);

  const finish = useCallback(async () => {
    await onCompleted();
    await loadRecord();
  }, [onCompleted, loadRecord]);

  const execute = useCallback(async () => {
    if (!actionable || !action || !selectedOrg || !selectedObject || !walletAddress) return;
    const objectId = selectedObject.id;

    if (action.kind === 'register') {
      // Two transactions, shown as two. The object has to exist on chain
      // before anything can be bound to it, and a country admin has to
      // sign that too — folding them into one button that silently signs
      // twice would hide a second fee and a second authorization.
      if (!registered) {
        const country = objectNumericCountry ?? countryCode;
        if (!country) return;
        const ok = await write.run({
          build: () => buildRegisterObject({
            caller: walletAddress,
            objectId,
            country,
            parentId: selectedObject.parentObjectId ?? null,
          }),
          successMessage: `Registered ${selectedObject.name || shortObjectId(objectId)} on chain`,
          refresh: loadRecord,
        });
        if (!ok) return;
        setRegistered(true);
        return; // let the operator see step one land before signing step two
      }
    }

    if (action.kind === 'register' || action.kind === 'propose') {
      await write.run({
        build: () => buildProposeBinding({
          objectId,
          caller: walletAddress,
          organizationId: selectedOrg.id,
          relationship,
          reasonHash: reasonHashArg,
        }),
        successMessage:
          `Proposed ${selectedOrg.name || shortOrgId(selectedOrg.id)} as `
          + `${relationshipLabel(relationship).toLowerCase()}`,
        mirror: { objectId, organizationId: selectedOrg.id },
        refresh: finish,
      });
      return;
    }

    if (action.kind === 'approve' || action.kind === 'reinstate') {
      await write.run({
        build: () => buildApproveBinding(objectId, walletAddress),
        successMessage: action.kind === 'reinstate' ? 'Binding reinstated' : 'Binding approved',
        mirror: { objectId, organizationId: selectedOrg.id },
        refresh: finish,
      });
      return;
    }

    if (action.kind === 'reassign') {
      await write.run({
        build: () => buildProposeReassign(objectId, walletAddress, selectedOrg.id, reasonHashArg),
        successMessage:
          `Reassignment to ${selectedOrg.name || shortOrgId(selectedOrg.id)} proposed`,
        refresh: finish,
      });
      return;
    }

    if (action.kind === 'complete_reassign') {
      await write.run({
        build: () => buildCompleteReassign(objectId, walletAddress),
        successMessage: 'Reassignment completed',
        mirror: { objectId, organizationId: record?.pendingTransfer?.toOrg ?? null },
        refresh: finish,
      });
    }
  }, [
    actionable, action, selectedOrg, selectedObject, walletAddress, registered,
    objectNumericCountry, countryCode, relationship, reasonHashArg, write,
    loadRecord, finish, record,
  ]);

  const cancelTransfer = useCallback(async () => {
    if (!selectedObject || !walletAddress) return;
    await write.run({
      build: () => buildCancelReassign(selectedObject.id, walletAddress),
      successMessage: 'Reassignment cancelled',
      refresh: finish,
    });
  }, [selectedObject, walletAddress, write, finish]);

  const actionLabel = useMemo(() => {
    if (!action) return 'Continue';
    switch (action.kind) {
      case 'register': return registered ? 'Propose binding' : 'Register object on chain';
      case 'propose': return 'Propose binding';
      case 'approve': return 'Approve binding';
      case 'reinstate': return 'Reinstate binding';
      case 'reassign': return 'Propose reassignment';
      case 'complete_reassign': return 'Complete reassignment';
      case 'already_bound': return 'Already bound';
      default: return 'Unavailable';
    }
  }, [action, registered]);

  // ── Render ─────────────────────────────────────────────────────────────────

  const title = mode === 'from-org' ? 'Bind spatial object' : 'Assign organization';

  return (
    <ModalShell
      title={title}
      onClose={onClose}
      footer={(
        <>
          <button onClick={onClose} style={button(true, 'plain')}>
            {write.state.phase === 'done' ? 'Close' : 'Cancel'}
          </button>
          {action?.kind === 'complete_reassign' && (
            <button onClick={cancelTransfer} disabled={write.busy} style={button(!write.busy, 'danger')}>
              Cancel reassignment
            </button>
          )}
          <button
            onClick={execute}
            disabled={!actionable || write.busy}
            style={button(
              !!actionable && !write.busy,
              action?.kind === 'reassign' ? 'warn' : 'primary',
            )}
          >
            {write.busy ? TX_PHASE_LABEL[write.state.phase] : actionLabel}
          </button>
        </>
      )}
    >
      {/* Organization */}
      <div style={{ marginBottom: '14px' }}>
        <div style={sectionTitle}>Organization</div>
        {mode === 'from-org' && fixedOrg ? (
          <OrgSummary org={fixedOrg} />
        ) : (
          <>
            <select
              value={selectedOrg?.id ?? ''}
              onChange={async (e) => {
                const id = e.target.value;
                const found = orgs.find((o) => o.id === id) ?? null;
                setSelectedOrg(found ?? (id ? await getOrganization(id) : null));
              }}
              style={{ ...input, width: '100%' }}
            >
              <option value="">
                {listLoading ? 'Loading organizations…' : 'Select an organization…'}
              </option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name || '(unnamed)'} · {o.alpha2 || o.numericCode || '—'} · {o.statusName}
                </option>
              ))}
            </select>
            {!listLoading && !orgs.length && (
              <div style={{ ...hint, marginTop: '6px' }}>
                No organizations are registered for this country yet. Register one in
                the country&apos;s Organizations section first.
              </div>
            )}
            {selectedOrg && <div style={{ marginTop: '8px' }}><OrgSummary org={selectedOrg} /></div>}
          </>
        )}
      </div>

      {/* Object */}
      <div style={{ marginBottom: '14px' }}>
        <div style={sectionTitle}>Spatial object</div>
        {mode === 'from-object' && fixedObject ? (
          <ObjectSummary object={fixedObject} record={record} />
        ) : (
          <>
            {objects.length > 8 && (
              <input
                value={objectQuery}
                onChange={(e) => setObjectQuery(e.target.value)}
                placeholder="Search by name, type or id"
                style={{ ...input, width: '100%', marginBottom: '6px' }}
              />
            )}
            <select
              value={selectedObject?.id ?? ''}
              onChange={(e) => {
                const row = objects.find((r) => r.id === e.target.value);
                setRegistered(false);
                setSelectedObject(row ? rowToDialogObject(row) : null);
              }}
              style={{ ...input, width: '100%' }}
            >
              <option value="">
                {listLoading ? 'Loading objects…' : 'Select a spatial object…'}
              </option>
              {filteredObjects.map((r) => (
                <option key={r.id} value={r.id}>
                  {describeRow(r)}
                </option>
              ))}
            </select>

            {listError && (
              <div style={{ marginTop: '6px' }}>
                <Banner tone="warn">
                  Objects could not be listed from GeoLink: {listError} You can still
                  enter an object id directly below.
                </Banner>
              </div>
            )}

            <div style={{ ...hint, marginTop: '6px' }}>
              {countryScoped && alpha2
                ? `Objects registered in ${alpha2}. `
                : 'All objects GeoLink knows about. '}
              {countryDataIncomplete && alpha2 && (
                <>
                  No object carries a country of {alpha2} in GeoLink, so the list is
                  not country-scoped — check the country before binding.{' '}
                </>
              )}
              An object must exist in GeoLink before it can be bound.
              {' '}
              <button
                onClick={() => setShowRawId((v) => !v)}
                style={{
                  border: 'none', background: 'none', padding: 0, color: '#007bff',
                  cursor: 'pointer', fontSize: '12px', textDecoration: 'underline',
                }}
              >
                {showRawId ? 'Hide id entry' : 'Enter an id instead'}
              </button>
            </div>

            {showRawId && (
              <div style={{ marginTop: '6px' }}>
                <input
                  value={rawObjectId}
                  onChange={(e) => setRawObjectId(e.target.value)}
                  onBlur={() => {
                    const id = normalizeObjectId(rawObjectId);
                    if (!id) return;
                    const uuid = objectIdToUuid(id)!;
                    const row = objects.find((r) => r.id.toLowerCase() === uuid);
                    setRegistered(false);
                    setSelectedObject(row ? rowToDialogObject(row) : {
                      id: uuid, name: null, countryIso2: alpha2,
                    });
                  }}
                  placeholder="Object UUID or 32 hex characters"
                  style={{ ...input, ...mono, width: '100%' }}
                />
                {rawObjectId.trim() && !normalizeObjectId(rawObjectId) && (
                  <div style={{ ...hint, color: '#a33' }}>
                    Not a UUID or 32 hex characters.
                  </div>
                )}
              </div>
            )}

            {selectedObject && (
              <div style={{ marginTop: '8px' }}>
                <ObjectSummary object={selectedObject} record={record} />
              </div>
            )}
          </>
        )}
      </div>

      {/* Relationship — only for writes that set one */}
      {(!action || action.kind === 'register' || action.kind === 'propose') && (
        <div style={{ marginBottom: '14px' }}>
          <div style={sectionTitle}>Relationship</div>
          <select
            value={relationship}
            onChange={(e) => setRelationship(e.target.value as RelationshipName)}
            style={{ ...input, width: '100%' }}
          >
            {(Object.keys(RELATIONSHIP) as RelationshipName[]).map((name) => (
              <option key={name} value={name}>{RELATIONSHIP_LABEL[name]}</option>
            ))}
          </select>
          <div style={{ ...hint, marginTop: '4px' }}>
            How the organization relates to the object. Changeable later without re-binding.
          </div>
        </div>
      )}

      {/* What will actually happen */}
      {action && (
        <div style={{ marginBottom: '14px' }}>
          <div style={sectionTitle}>What this does</div>
          <WorkflowExplanation
            action={action}
            registered={registered}
            orgName={selectedOrg?.name ?? null}
            record={record}
          />
        </div>
      )}

      {/* Eligibility */}
      {selectedOrg && (
        <div style={{ marginBottom: '14px' }}>
          <div style={sectionTitle}>Organization eligibility</div>
          <CapabilityList
            canOwn={eligibility.canOwnObjects}
            canOperate={eligibility.canOperateObjects}
            canReceive={eligibility.canReceiveFunds}
          />
          {limitations.length > 0 && (
            <ul style={{ ...hint, margin: '8px 0 0 0', paddingLeft: '18px' }}>
              {limitations.map((line) => <li key={line} style={{ marginBottom: '3px' }}>{line}</li>)}
            </ul>
          )}
        </div>
      )}

      {/* Advanced */}
      {(!action || ['register', 'propose', 'reassign'].includes(action.kind)) && (
        <div style={{ marginBottom: '10px' }}>
          <button
            onClick={() => setShowAdvanced((v) => !v)}
            style={{
              border: 'none', background: 'none', padding: 0, color: '#007bff',
              cursor: 'pointer', fontSize: '12px',
            }}
          >
            {showAdvanced ? '▾' : '▸'} Evidence reference
          </button>
          {showAdvanced && (
            <div style={{ marginTop: '6px' }}>
              <input
                value={reasonHash}
                onChange={(e) => setReasonHash(e.target.value)}
                placeholder="64-character hex hash (optional)"
                style={{ ...input, ...mono, width: '100%' }}
              />
              <div style={{ ...hint, marginTop: '4px' }}>
                A hash only. Documents and notes belong in GeoLink; the chain stores
                32 bytes so the evidence can be proved unchanged, not the evidence itself.
              </div>
              {!reasonHashValid && (
                <div style={{ ...hint, color: '#a33' }}>Must be exactly 64 hex characters.</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Blockers and transaction state */}
      {blockers.length > 0 && write.state.phase === 'idle' && (
        <Banner tone="warn">
          <ul style={{ margin: 0, paddingLeft: '18px' }}>
            {blockers.map((b) => <li key={b}>{b}</li>)}
          </ul>
        </Banner>
      )}
      {write.state.error && <Banner tone="error">{write.state.error}</Banner>}
      {write.state.warning && <Banner tone="warn">{write.state.warning}</Banner>}
      {write.state.notice && <Banner tone="ok">{write.state.notice}</Banner>}
      {write.busy && (
        <Banner tone="info">
          {TX_PHASE_LABEL[write.state.phase]} Do not close this dialog.
        </Banner>
      )}
    </ModalShell>
  );
};

// ── Pieces ───────────────────────────────────────────────────────────────────

function rowToDialogObject(row: SpatialObjectAdminRow): BindDialogObject {
  return {
    id: row.id,
    name: row.name ?? null,
    objectType: row.objectType ?? null,
    countryIso2: row.countryCode ?? null,
    parentObjectId: row.parentObjectId ?? null,
    organizationId: readMirrorOrgId(row),
  };
}

function describeRow(row: SpatialObjectAdminRow): string {
  const bits = [row.name || '(unnamed)', row.objectType, row.countryCode, row.id.slice(0, 8)];
  return bits.filter(Boolean).join(' · ');
}

const OrgSummary: React.FC<{ org: Organization }> = ({ org }) => (
  <div style={{ border: '1px solid #eee', borderRadius: '4px', padding: '8px 10px' }}>
    <div style={{ fontSize: '13px', fontWeight: 600 }}>{org.name || '(unnamed)'}</div>
    <div style={{ ...hint, ...mono }}>
      {shortOrgId(org.id)}… · {org.alpha2 || org.numericCode || '—'} · {org.statusName}
    </div>
  </div>
);

const ObjectSummary: React.FC<{
  object: BindDialogObject;
  record: SpatialObjectRecord | null;
}> = ({ object, record }) => (
  <div style={{ border: '1px solid #eee', borderRadius: '4px', padding: '8px 10px' }}>
    <div style={{ fontSize: '13px', fontWeight: 600 }}>{object.name || '(unnamed)'}</div>
    <div style={{ ...hint, ...mono }}>
      {object.objectType ? `${object.objectType} · ` : ''}
      {object.countryIso2 || record?.alpha2 || '—'} · {object.id.slice(0, 8)}…
    </div>
    {object.parentObjectId && (
      <div style={hint}>Child of {object.parentObjectId.slice(0, 8)}…</div>
    )}
    <div style={{ marginTop: '6px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
      {record
        ? <Pill tone="good">On chain</Pill>
        : <Pill tone="neutral">Not registered on chain</Pill>}
      {record?.binding?.organizationId && (
        <Pill tone="info">
          Held by {record.binding.organizationId.slice(0, 8)}… ({record.binding.statusName})
        </Pill>
      )}
      {object.organizationId && !record?.binding && (
        <Pill tone="warn">GeoLink lists {object.organizationId.slice(0, 8)}…</Pill>
      )}
    </div>
  </div>
);

/**
 * The consequences of the pending action, spelled out.
 *
 * The reassignment case gets the most words because it is the one that
 * destroys state the operator may not be thinking about: completing a
 * transfer bumps the operator epoch, which revokes every delegated
 * operator on the object at once.
 */
const WorkflowExplanation: React.FC<{
  action: BindAction;
  registered: boolean;
  orgName: string | null;
  record: SpatialObjectRecord | null;
}> = ({ action, registered, orgName, record }) => {
  const org = orgName || 'the organization';

  if (action.kind === 'register') {
    return (
      <Banner tone="info">
        <strong>Two transactions.</strong> This object exists in GeoLink but not in the
        spatial registry, so it is registered on chain first
        {registered ? ' — done' : ''}, then {org} is proposed as its organization.
        You will be asked to sign {registered ? 'once more' : 'twice'}.
      </Banner>
    );
  }

  if (action.kind === 'reassign') {
    return (
      <Banner tone="warn">
        <strong>This is a reassignment, not a new binding.</strong>
        <ul style={{ margin: '6px 0 0 0', paddingLeft: '18px' }}>
          <li>
            Currently held by{' '}
            {action.currentOrganizationId
              ? `${action.currentOrganizationId.slice(0, 8)}…`
              : 'another organization'}
            {record?.binding?.statusName ? ` (${record.binding.statusName})` : ''}.
          </li>
          <li>Proposed new organization: {org}.</li>
          <li>
            Two steps: proposing records the intent, completing it moves the object.
            The incoming organization&apos;s eligibility is re-checked at completion,
            not now.
          </li>
          <li>
            Completing clears every delegated operator on this object, and the
            object&apos;s beneficiary with them.
          </li>
          <li>
            Child objects that inherit from this one follow the new organization
            automatically; children with their own binding are unaffected.
          </li>
        </ul>
      </Banner>
    );
  }

  if (action.kind === 'complete_reassign') {
    return (
      <Banner tone="warn">
        {action.reason}
        {/*
          Named before signing rather than discovered afterwards. Completion
          bumps the object's operator epoch, which invalidates every grant in
          one write — so this is the last moment at which the operator can be
          told what is about to stop working.
        */}
        <ul style={{ margin: '6px 0 0 0', paddingLeft: '18px' }}>
          <li>
            <strong>Every delegated operator grant on this object is revoked.</strong>{' '}
            Anyone holding MANAGE_OBJECT, MANAGE_RULES or MANAGE_FUNDS here loses it
            the moment this transaction lands, and the incoming organization has to
            re-issue whatever it wants to keep.
          </li>
          <li>
            {record?.beneficiary ? (
              <>
                The payout beneficiary is cleared. It is currently{' '}
                <span style={{ fontFamily: 'monospace' }}>
                  {record.beneficiary.slice(0, 6)}…{record.beneficiary.slice(-4)}
                </span>
                , and value routed here will have nowhere to go until the new
                organization sets one.
              </>
            ) : (
              'No payout beneficiary is set, so there is none to clear.'
            )}
          </li>
          <li>
            Execution rules attached to this object are not deleted, but who may
            change them is re-evaluated — authority follows the new organization.
          </li>
          <li>
            Children that inherit from this object move with it. Their own delegated
            grants are held per object and are <em>not</em> revoked by this
            transaction; revoke them separately if the handover should be complete.
          </li>
          <li style={{ color: '#6c757d' }}>
            The registry cannot enumerate grants, so this cannot list the specific
            wallets affected — only that all of them are.
          </li>
        </ul>
      </Banner>
    );
  }

  if (action.kind === 'blocked' || action.kind === 'already_bound') {
    return (
      <Banner tone={action.kind === 'blocked' ? 'warn' : 'ok'}>
        {action.reason || `This object is already bound to ${org} and approved.`}
      </Banner>
    );
  }

  if (action.kind === 'approve' || action.kind === 'reinstate') {
    return (
      <Banner tone="info">
        {action.reason
          || 'Approving makes the proposed binding live. The registry re-checks that '
            + 'the organization may own objects and that its country matches this one.'}
      </Banner>
    );
  }

  return (
    <Banner tone="info">
      {action.reason
        || 'Records a proposed binding. It confers no authority until a country or '
          + 'protocol admin approves it.'}
    </Banner>
  );
};
